import type {
  BlockwalkerActivity,
  CampaignBlockName,
} from '@goldfishcode/kingmakerdata-api-sdk/libs/api/campaign/models';
import type {
  DashboardChartData,
  DashboardFilterParams,
  DashboardSettings,
  Tag,
  TagColor,
  TagIcon,
} from '@goldfishcode/kingmakerdata-api-sdk/libs/api/dashboard/models';
import type { Question } from '@goldfishcode/kingmakerdata-api-sdk/libs/api/survey/models';
import type { ValueLabel } from 'src/models';

import type { SyncDataStatus, SyncDataType } from '../socket/model';

export interface ChartDataType {
  label: string[];
  datasets: number[];
}

export interface SurveyChartDataType {
  [surveyId: string]: Record<string, Record<string, number>>;
}

export interface SurveyNames {
  id: string;
  title?: string;
}

export interface TagConfig {
  colors: TagColor[];
  icons: TagIcon[];
}

export interface FilterParam {
  name: string;
  data?: ChartDataType | Record<string, number> | null;
}

export type SyncType = SyncDataType;

export type SyncStatus = SyncDataStatus;

export interface SyncingData {
  type: SyncType;
  status: SyncStatus;
  self?: boolean;
}

export interface ImportContact {
  id: string;
  type: SyncType;
}

export interface FieldOperationsQuery {
  isLoading: boolean;
  searchValue: string;
  isBlockwalking: boolean;
}

export interface Dashboard {
  dashboardSettings: DashboardSettings | null;
  dashboardChartData: DashboardChartData | null;
  // Loading
  firstLoad: boolean;
  countLoading: number;
  // Filter params
  dashboardFilterParams: DashboardFilterParams;
  // Chart data from left to right
  countyDropdown: string[];
  precinctDropdown: string[] | number[];
  selectControlCountyDropdown: string[];
  selectControlPrecinctDropdown: string[] | number[];
  ageRange?: string[] | [];
  ageBracket?: ChartDataType | null;
  gender?: ChartDataType | null;
  est_party?: ChartDataType | null;
  voted?: ChartDataType | null;
  cityCouncil?: ChartDataType | null;
  expected_remaining_voters_chart?: Record<string, number> | null;
  tagDropdown: string[];
  selectedTags: ChartDataType | null;
  voterLikelihoodBar?: ChartDataType | null;
  preferredVotingStyleBar?: ChartDataType | null;
  contact_bar?: ChartDataType | null;
  contact_poll?: ChartDataType | null;
  projected_outcomes_chart?: ChartDataType | null;
  // State for actions
  filterParam?: FilterParam;
  isFilterChartsByChart: boolean;
  isClearFilter: boolean;
  isApplyRemoveFilter: boolean;
  isExportData: boolean;
  isExportDataClicked: boolean;
  isImportData: boolean;
  isImportDataClicked: boolean;
  cityCouncilColors?: string[];
  isCreateBlockwalksWarning: boolean;
  isVisibleCharts: boolean;
  isSwitchCandidate: boolean;
  isLoadingChart: boolean;
  isLoadingDefault: boolean; // for running getting dashboard data
  syncingData?: SyncingData;
  importContact?: ImportContact;
  // Storing tags
  tagConfig: TagConfig;
  tags: Tag[];
  tagAndOr: 'AND' | 'OR';
  // Saving keys of changed filter params
  changedKeys: string[];
  survey_chart: SurveyChartDataType | null;
  surveyAndOr: 'AND' | 'OR';
  // Field Operations
  fieldOperationsQuery: Partial<FieldOperationsQuery>;
  campaignBlocks: CampaignBlockName[];
  fieldOperationsSelected: ValueLabel<string> | null;
  assignedToField?: boolean;

  // Activity
  listActivity: BlockwalkerActivity[];
  activityQuery?: {
    start_date?: string | null;
    end_date?: string | null;
    page?: number;
    hasMore?: boolean;
    isLoading?: boolean;
  };

  // Survey
  questions: Question[];
  loadingQuestions: boolean;

  questionsBySurvey: Record<string, Question[]>;
  surveyNames?: SurveyNames[];
  loadingSurveyNames: boolean;
  selectedSurvey: string[];
  lalVoterIds: string[];
}

export enum Party {
  STRONG_REPUBLICAN = 'Strong Republican',
  SOFT_REPUBLICAN = 'Soft Republican',
  SWING = 'Swing',
  STRONG_DEMOCRAT = 'Strong Democrat',
  SOFT_DEMOCRAT = 'Soft Democrat',
}

export enum VotePrediction {
  LIKELY = 'Likely',
  POSSIBLE = 'Possible',
  UNLIKELY = 'Unlikely',
  HIGHLY_LIKELY = 'Highly Likely',
  HIGHLY_UNLIKELY = 'Highly Unlikely',
  EXPECTED = 'Expected',
  SKIP = 'Skip',
}
